import { Injectable, OnDestroy } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  HttpTransportType,
} from '@microsoft/signalr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { OnboardingHubEvent } from '../enum/enums';
import {
  AppStepReviewStatus,
} from '../interfaces/onboarding-notification.interfaces';
import {
  mapNotificationToStepUpdates,
  applyStepUpdates,
} from '../interfaces/onboarding-notification.mapper';
import { environment } from '../../../environments/environment';

export interface OnboardingNotification {
  applicationId: string;
  status: string;
  message: string;
  timestamp: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class SignalRService implements OnDestroy {
  private readonly hubUrl = `${environment.baseUrl}/lms/hubs/onboarding`;

  private hubConnection: HubConnection | null = null;

  private readonly _connectionState$ = new BehaviorSubject<HubConnectionState>(
    HubConnectionState.Disconnected
  );
  public readonly connectionState$: Observable<HubConnectionState> =
    this._connectionState$.asObservable();

  private readonly _notification$ = new Subject<OnboardingNotification>();
  public readonly notification$: Observable<OnboardingNotification> =
    this._notification$.asObservable();

  private readonly _rawNotification$ = new Subject<unknown>();
  public readonly rawNotification$: Observable<unknown> =
    this._rawNotification$.asObservable();

  private readonly _stepReviewStatusByApp$ =
    new BehaviorSubject<Readonly<Record<number, AppStepReviewStatus>>>({});
  public readonly stepReviewStatus$: Observable<AppStepReviewStatus> =
    this._stepReviewStatusByApp$.pipe(
      map((byApp) => {
        if (this._activeApplicationId == null) return {};
        return byApp[this._activeApplicationId] ?? {};
      }),
    );

  private readonly _error$ = new Subject<string>();
  public readonly error$: Observable<string> = this._error$.asObservable();
  private _activeApplicationId: number | null = null;

  async startConnection(tokenFactory: () => string): Promise<void> {
    if (
      this.hubConnection &&
      this.hubConnection.state !== HubConnectionState.Disconnected
    ) {
      console.warn('[SignalR] Connection already active — skipping.');
      return;
    }

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(this.hubUrl, {
        accessTokenFactory: () => tokenFactory(),
        transport:
          HttpTransportType.WebSockets | HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          const delays = [0, 2000, 5000, 10000, 30000];
          return retryContext.previousRetryCount < delays.length
            ? delays[retryContext.previousRetryCount]
            : 60000;
        },
      })
      .configureLogging(LogLevel.Information)
      .build();

    this.registerLifecycleCallbacks();
    this.registerHubListeners();

    try {
      await this.hubConnection.start();
      console.log('[SignalR] Connected via', this.hubConnection.connectionId);
      this._connectionState$.next(this.hubConnection.state);
    } catch (err) {
      console.error('[SignalR] Connection failed:', err);
      this._connectionState$.next(HubConnectionState.Disconnected);
      this._error$.next(
        err instanceof Error ? err.message : 'Connection failed'
      );
    }
  }

  async stopConnection(): Promise<void> {
    if (this.hubConnection) {
      await this.hubConnection.stop();
      this.hubConnection = null;
      this._connectionState$.next(HubConnectionState.Disconnected);
      console.log('[SignalR] Disconnected.');
    }
  }

  ngOnDestroy(): void {
    this.stopConnection();
    this._notification$.complete();
    this._rawNotification$.complete();
    this._stepReviewStatusByApp$.complete();
    this._connectionState$.complete();
    this._error$.complete();
  }

  private registerHubListeners(): void {
    if (!this.hubConnection) return;

    this.hubConnection.on(
      'ReceiveOnboardingUpdate',
      (data: unknown) => {
        console.log('[SignalR] Received:', data);
        this._notification$.next(data as OnboardingNotification);
        this._rawNotification$.next(data);
        this.accumulateReviewState(data);
      }
    );

    const hubEvents: OnboardingHubEvent[] = [
      OnboardingHubEvent.EntityCompleted,
      OnboardingHubEvent.EntityIncomplete,
    ];

    for (const event of hubEvents) {
      this.hubConnection.on(event, (data: unknown) => {
        console.log(`[SignalR] ${event}:`, data);
        this._notification$.next(data as OnboardingNotification);
        this._rawNotification$.next(data);
        this.accumulateReviewState(data);
      });
    }
  }

  private accumulateReviewState(raw: unknown): void {
    if (!this.isForActiveApplication(raw)) return;
    const updates = mapNotificationToStepUpdates(raw);
    if (updates.length === 0) return;
    const applicationId = this._activeApplicationId;
    if (applicationId == null) return;
    const byApp = this._stepReviewStatusByApp$.value;
    const current = byApp[applicationId] ?? {};
    const next = applyStepUpdates(current, updates);
    this._stepReviewStatusByApp$.next({
      ...byApp,
      [applicationId]: next,
    });
  }

  clearStepReviewStatus(): void {
    if (this._activeApplicationId == null) return;
    const byApp = this._stepReviewStatusByApp$.value;
    this._stepReviewStatusByApp$.next({
      ...byApp,
      [this._activeApplicationId]: {},
    });
  }

  setActiveApplication(applicationId: number | null): void {
    if (this._activeApplicationId === applicationId) return;
    this._activeApplicationId = applicationId;
    this._stepReviewStatusByApp$.next({
      ...this._stepReviewStatusByApp$.value,
    });
  }

  setInitialStepReviewStatus(
    applicationId: number,
    statusByRoute: AppStepReviewStatus,
  ): void {
    const byApp = this._stepReviewStatusByApp$.value;
    this._stepReviewStatusByApp$.next({
      ...byApp,
      [applicationId]: { ...statusByRoute },
    });
  }

  private isForActiveApplication(raw: unknown): boolean {
    if (this._activeApplicationId == null) return false;
    if (!raw || typeof raw !== 'object') return false;
    const applicationIdValue = (raw as Record<string, unknown>)['applicationId'];
    const parsedId = Number(applicationIdValue);
    if (!Number.isFinite(parsedId)) return false;
    return parsedId === this._activeApplicationId;
  }

  private registerLifecycleCallbacks(): void {
    if (!this.hubConnection) return;

    this.hubConnection.onreconnecting((error) => {
      console.warn('[SignalR] Reconnecting...', error?.message);
      this._connectionState$.next(HubConnectionState.Reconnecting);
    });

    this.hubConnection.onreconnected((connectionId) => {
      console.log('[SignalR] Reconnected:', connectionId);
      this._connectionState$.next(HubConnectionState.Connected);
    });

    this.hubConnection.onclose((error) => {
      console.warn('[SignalR] Connection closed.', error?.message);
      this._connectionState$.next(HubConnectionState.Disconnected);
      if (error) {
        this._error$.next(error.message);
      }
    });
  }
}
